// src/components/dashboard/BookInventory.jsx

import { useEffect, useState, useCallback } from "react";
import BookService from "../../services/dashboard/BookManager"; // ← uses your existing BookManager
import BookCopyService from "../../services/dashboard/BookManager"; // to fetch copy inventory
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import debounce from "lodash.debounce";
import config from "../../config";
import { handleApiResponse } from "../../utils/apiResponseHandler";
import Pagination from "../home/exploreBooksComponents/Pagination";
import { useSelector } from "react-redux";

export default function BookInventory() {
  const [copies, setCopies] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const PAGE_SIZE = 15;
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId) || null;

  useEffect(() => {
    fetchData();
  }, [page, selectedBook]);

  const fetchData = async (newPage = page, bookFilter = selectedBook) => {
    setLoading(true);
    try {
      // 1. FETCH ALL BOOKS FOR DROPDOWN (uses BookManager.getAllBooks)
      const bookRes = await BookService.getAllBooks({ page: 0, size: 1000, userId });
      const booksData = handleApiResponse(bookRes) || [];
      // If your backend returns { content: [...], totalPages, number }, we pick content
      setAllBooks(booksData.content || booksData);

      // 2. FETCH COPIES (inventory) – either ALL or FILTERED BY bookId
      let copyRes;
      if (!bookFilter || bookFilter === "ALL") {
        copyRes = await BookCopyService.getAllCopies({ page: newPage, size: PAGE_SIZE });
      } else {
        copyRes = await BookCopyService.getCopiesByBookId(bookFilter, {
          page: newPage,
          size: PAGE_SIZE,
        });
      }

      const copiesData = handleApiResponse(copyRes) || { content: [], totalPages: 0, number: 0 };
      if (copiesData.content) {
        setCopies(copiesData.content);
        setTotalPages(copiesData.totalPages || 0);
        setPage(copiesData.number || 0);
      } else {
        // in case search returns non-paginated array
        setCopies(copiesData);
        setTotalPages(0);
        setPage(0);
      }
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (copyId) => {
    if (window.confirm("Are you sure you want to delete this copy?")) {
      try {
        const res = await BookCopyService.deleteCopy(copyId);
        const success = handleApiResponse(res, "Copy deleted successfully!");
        if (success !== null) {
          fetchData();
        }
      } catch (error) {
        console.error("Error deleting copy:", error);
      }
    }
  };

  const handleEdit = (copy) => {
    navigate(`/copyForm/${copy.copyId}`, { state: { copy } });
  };

  const getBookTitle = (bookId) => {
    const book = allBooks.find((b) => b.bookId === bookId);
    return book ? book.bookName : bookId;
  };

  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term.trim() === "") {
        fetchData(0, selectedBook);
      } else {
        try {
          const res = await BookCopyService.searchCopies(term);
          const searchResults = handleApiResponse(res) || [];
          setCopies(searchResults);
          setTotalPages(0);
          setPage(0);
        } catch (error) {
          console.error("Search failed:", error);
        }
      }
    }, 300),
    [selectedBook]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  return (
    <div className="p-6 bg-primary-shade1 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        {/* Title */}
        <h1 className="text-4xl font-bold text-text-muted tracking-tight">
          Inventory (Book Copies)
        </h1>

        {/* Actions: Search, Filter, Add Copy */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search copies..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-primary-shade3 bg-white text-sm text-text-muted shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-shade4 pointer-events-none"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1011 18.5a7.5 7.5 0 005.65-2.85z"
              />
            </svg>
          </div>

          {/* Book Filter Dropdown */}
          <select
            id="bookFilter"
            value={selectedBook}
            onChange={(e) => {
              const selected = e.target.value;
              setSelectedBook(selected);
              setPage(0);
            }}
            className="px-4 py-2 rounded-lg border border-primary-shade3 bg-white text-sm text-text-muted shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="ALL">All Books</option>
            {allBooks.map((book) => (
              <option key={book.bookId} value={book.bookId}>
                {book.bookName}
              </option>
            ))}
          </select>

          {/* Add Copy Button */}
          <button
            onClick={() => navigate("/copyForm")}
            className="bg-primary hover:bg-primary-shade4 text-white px-4 py-2 rounded-lg shadow-md transition-all duration-300 ease-in-out flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Copy
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-primary text-lg animate-pulse">
          Loading inventory...
        </p>
      ) : copies.length === 0 ? (
        <p className="text-primary-shade5 text-lg font-medium">
          No copies available.
        </p>
      ) : (
        <>
          <div className="overflow-auto rounded-lg shadow border border-primary-shade2">
            <table className="min-w-full bg-white text-sm text-left text-gray-700">
              <thead className="bg-primary text-white uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-6 py-3">Cover</th>
                  <th className="px-6 py-3">Copy ID</th>
                  <th className="px-6 py-3">Book Title</th>
                  <th className="px-6 py-3">Barcode/Serial</th>
                  <th className="px-6 py-3">Location</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {copies.map((copy, idx) => (
                  <tr
                    key={copy.copyId}
                    className={`transition-all duration-200 hover:bg-primary-shade1 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-3">
                      {copy.bookImages?.length > 0 && (
                        <img
                          src={`${config.url}${copy.bookImages[0]}`}
                          alt={copy.bookName}
                          className="w-16 h-20 object-fit rounded border border-primary-shade3 shadow-sm"
                        />
                      )}
                    </td>
                    <td className="px-6 py-3 font-medium text-text-muted">
                      {copy.copyId}
                    </td>
                    <td className="px-6 py-3">
                      {getBookTitle(copy.bookId)}
                    </td>
                    <td className="px-6 py-3">{copy.barcode || "N/A"}</td>
                    <td className="px-6 py-3">{copy.location || "N/A"}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          copy.status === "AVAILABLE"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {copy.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 align-middle">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => handleEdit(copy)}
                          className="bg-[#FF7236] text-white p-2 rounded-full hover:bg-[#EB5E22] transition"
                          title="Edit Copy"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(copy.copyId)}
                          className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition"
                          title="Delete Copy"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-end items-center mt-4">
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={(newPage) => setPage(newPage)}
            />
          </div>
        </>
      )}
    </div>
  );
}
